import { useState, useRef, useEffect } from "react";
import { useAuth } from "../lib/AuthContext";
import { apiUpload, apiGet } from "../lib/api";
import { COLORS } from "../lib/colors";

export default function DocumentUpload({ institutionId }) {
  const { session } = useAuth();
  const token = session?.access_token;
  const fileInputRef = useRef(null);

  const [files, setFiles] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [results, setResults] = useState(null);
  const [existing, setExisting] = useState([]);

  useEffect(() => {
    if (institutionId && token) loadExisting();
  }, [institutionId, token]);

  const loadExisting = async () => {
    try {
      const data = await apiGet(`/institution/${institutionId}/documents`, token);
      setExisting(Array.isArray(data) ? data : data.documents || []);
    } catch {
      // endpoint may not exist yet — that's fine
      setExisting([]);
    }
  };

  const handleFiles = (e) => {
    setFiles(Array.from(e.target.files));
    setResults(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const dropped = Array.from(e.dataTransfer.files).filter(
      (f) => f.name.endsWith(".pdf") || f.name.endsWith(".docx")
    );
    if (dropped.length) {
      setFiles(dropped);
      setResults(null);
    }
  };

  const totalSizeMB = (files.reduce((sum, f) => sum + f.size, 0) / 1024 / 1024).toFixed(1);

  const uploadAll = async () => {
    setProcessing(true);
    setProgress({ current: 0, total: files.length });
    const res = { success: 0, failed: 0, details: [] };

    for (let i = 0; i < files.length; i++) {
      setProgress({ current: i + 1, total: files.length });
      const file = files[i];
      const formData = new FormData();
      formData.append("file", file);

      try {
        await apiUpload(`/institution/${institutionId}/document`, formData, token);
        res.success++;
        res.details.push({ filename: file.name, status: "success" });
      } catch (err) {
        res.failed++;
        res.details.push({ filename: file.name, status: "failed", message: err.message });
      }
    }

    setResults(res);
    setProcessing(false);
    loadExisting();
  };

  const clearResults = () => {
    setResults(null);
    setFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  if (!institutionId) {
    return (
      <div style={{
        backgroundColor: COLORS.orangeLight, color: COLORS.orange,
        padding: "12px 16px", borderRadius: 8, fontSize: 14,
      }}>
        Please select an institution in Setup first.
      </div>
    );
  }

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: COLORS.text, marginBottom: 4 }}>
        Institution Documents
      </h2>
      <p style={{ fontSize: 14, color: COLORS.textMuted, marginBottom: 24 }}>
        Upload internal documents that contain programme data not available on public websites &mdash;
        ILO matrices, curriculum maps, annual reports, strategic plans, fact sheets, assessment frameworks, etc.
      </p>

      {/* Upload area */}
      <div
        style={{
          border: `2px dashed ${COLORS.border}`, borderRadius: 12, padding: 32,
          textAlign: "center", marginBottom: 16, cursor: "pointer",
          backgroundColor: COLORS.surfaceAlt,
        }}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.docx"
          onChange={handleFiles}
          style={{ display: "none" }}
        />
        <div style={{ fontSize: 28, marginBottom: 8 }}>&#x1F4C4;</div>
        <p style={{ fontSize: 14, color: COLORS.textSecondary }}>
          Drop documents here, or click to browse
        </p>
        <p style={{ fontSize: 12, color: COLORS.textMuted }}>PDF or DOCX &mdash; annual reports, ILO documents, curriculum data, fact sheets</p>
      </div>

      {/* File list + upload button */}
      {files.length > 0 && !results && (
        <div style={{ marginBottom: 16 }}>
          <p style={{ fontSize: 13, color: COLORS.textSecondary, marginBottom: 8 }}>
            {files.length} file{files.length > 1 ? "s" : ""} selected ({totalSizeMB} MB)
          </p>

          <div style={{
            maxHeight: 160, overflowY: "auto", marginBottom: 12,
            border: `1px solid ${COLORS.borderLight}`, borderRadius: 8, padding: 8,
          }}>
            {files.map((f, i) => (
              <div key={i} style={{
                padding: "4px 0", fontSize: 13, color: COLORS.text,
                borderBottom: i < files.length - 1 ? `1px solid ${COLORS.borderLight}` : "none",
              }}>
                {f.name} <span style={{ color: COLORS.textMuted }}>({(f.size / 1024 / 1024).toFixed(1)} MB)</span>
              </div>
            ))}
          </div>

          {processing && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 13, color: COLORS.textSecondary, marginBottom: 4 }}>
                Uploading {progress.current} of {progress.total}...
              </div>
              <div style={{ height: 6, backgroundColor: COLORS.surfaceAlt, borderRadius: 3 }}>
                <div style={{
                  height: "100%", borderRadius: 3, backgroundColor: COLORS.accent,
                  width: `${(progress.current / progress.total) * 100}%`,
                  transition: "width 0.3s ease",
                }} />
              </div>
            </div>
          )}

          <button
            onClick={uploadAll}
            disabled={processing}
            style={{
              padding: "10px 24px", fontSize: 14, fontWeight: 600,
              backgroundColor: COLORS.accent, color: "#fff",
              border: "none", borderRadius: 8,
              cursor: processing ? "not-allowed" : "pointer",
              opacity: processing ? 0.7 : 1,
            }}
          >{processing ? "Uploading..." : "Upload Documents"}</button>
        </div>
      )}

      {/* Upload results */}
      {results && (
        <div style={{
          backgroundColor: COLORS.surface, borderRadius: 12,
          border: `1px solid ${COLORS.border}`, padding: 20, marginBottom: 24,
        }}>
          <div style={{ display: "flex", gap: 24, marginBottom: 16 }}>
            <span style={{ fontSize: 14, color: COLORS.green, fontWeight: 600 }}>
              {results.success} uploaded
            </span>
            {results.failed > 0 && (
              <span style={{ fontSize: 14, color: COLORS.red, fontWeight: 600 }}>
                {results.failed} failed
              </span>
            )}
          </div>

          <div style={{ maxHeight: 200, overflowY: "auto" }}>
            {results.details.map((d, i) => (
              <div key={i} style={{
                padding: "6px 0", borderBottom: `1px solid ${COLORS.borderLight}`,
                fontSize: 13, display: "flex", justifyContent: "space-between",
              }}>
                <span style={{ color: COLORS.text }}>{d.filename}</span>
                <span style={{
                  color: d.status === "success" ? COLORS.green : COLORS.red,
                }}>
                  {d.status === "success" ? "Uploaded" : d.message}
                </span>
              </div>
            ))}
          </div>

          <button
            onClick={clearResults}
            style={{
              marginTop: 12, padding: "8px 16px", fontSize: 13,
              backgroundColor: COLORS.surfaceAlt, color: COLORS.textSecondary,
              border: `1px solid ${COLORS.border}`, borderRadius: 6, cursor: "pointer",
            }}
          >Upload More</button>
        </div>
      )}

      {/* Existing documents */}
      {existing.length > 0 && (
        <div style={{
          backgroundColor: COLORS.surface, borderRadius: 12,
          border: `1px solid ${COLORS.border}`, padding: 20,
        }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text, marginBottom: 12 }}>
            Uploaded Documents ({existing.length})
          </div>
          <div style={{ maxHeight: 240, overflowY: "auto" }}>
            {existing.map((doc, i) => (
              <div key={doc.id || i} style={{
                padding: "8px 0",
                borderBottom: i < existing.length - 1 ? `1px solid ${COLORS.borderLight}` : "none",
                fontSize: 13, display: "flex", justifyContent: "space-between", alignItems: "center",
              }}>
                <span style={{ color: COLORS.text }}>{doc.filename}</span>
                <span style={{ color: COLORS.textMuted, fontSize: 12 }}>
                  {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : ""}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
